import React, { useState, useEffect, lazy, Suspense } from 'react';
import api from '../utils/api';

// Lazy-load the actual map so Leaflet doesn't crash the whole app on import
const MapView = lazy(() => import('../components/MapView'));

class MapErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="flex items-center justify-center h-full text-red-500 p-8">
          <div className="text-center">
            <p className="font-semibold mb-2">Map failed to load</p>
            <p className="text-sm text-secondary-500">{this.state.error.message}</p>
            <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm">
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function DeviceMap() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/devices/locations')
      .then(r => setDevices(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const validDevices = devices.filter(d => {
    const lat = parseFloat(d.latitude);
    const lng = parseFloat(d.longitude);
    return !isNaN(lat) && !isNaN(lng);
  });

  const center = validDevices.length > 0
    ? [
        validDevices.reduce((s, d) => s + parseFloat(d.latitude), 0) / validDevices.length,
        validDevices.reduce((s, d) => s + parseFloat(d.longitude), 0) / validDevices.length,
      ]
    : [29.7604, -95.3698]; // Houston default

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">Device Map</h1>
          <p className="text-secondary-500 text-sm mt-1">{devices.length} devices with location data</p>
        </div>
        <div className="flex items-center gap-3 sm:gap-4 text-xs text-secondary-500 flex-wrap">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500 inline-block"></span> Active</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span> Inactive</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500 inline-block"></span> Maintenance</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-600 inline-block"></span> Alert</span>
        </div>
      </div>

      <p className="text-xs text-secondary-400 -mt-1 sm:hidden">
        Tip: tap a numbered cluster to spread out devices at the same location.
      </p>

      <div
        className="bg-white rounded-xl shadow-card overflow-hidden"
        style={{ height: 'min(calc(100dvh - 200px), 80vh)', minHeight: 360 }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full text-secondary-400">Loading map...</div>
        ) : (
          <MapErrorBoundary>
            <Suspense fallback={<div className="flex items-center justify-center h-full text-secondary-400">Loading map component...</div>}>
              <MapView devices={devices} center={center} />
            </Suspense>
          </MapErrorBoundary>
        )}
      </div>
    </div>
  );
}
